/**
 * Navigation model for the responsive AppShell — pure (no React) so the
 * role→items matrix is unit-testable and can't silently regress.
 */
import {
  Activity,
  BarChart3,
  Bell,
  Calendar,
  CalendarDays,
  CalendarRange,
  Car,
  ClipboardList,
  Home,
  MapPin,
  MessagesSquare,
  PackageCheck,
  Send,
  Settings,
  ShieldCheck,
  Truck,
  User,
  Users,
} from 'lucide-react';

export type Role = 'crew' | 'supervisor' | 'pm' | 'sales' | 'admin';

/** Sidebar sections, in render order. Only roles whose nav is long enough to
 *  earn headers (supervisor/admin) render these; short navs ignore them and
 *  stay flat. Header labels live here so they can't drift from the keys. */
export type NavGroup = 'my-work' | 'operations' | 'resources' | 'reporting' | 'admin';

export const NAV_GROUPS: { key: NavGroup; label: string }[] = [
  { key: 'my-work', label: 'My work' },
  { key: 'operations', label: 'Operations' },
  { key: 'resources', label: 'Resources' },
  { key: 'reporting', label: 'Reporting' },
  { key: 'admin', label: 'Admin' },
];

export interface NavItem {
  to: string;
  label: string;
  icon: typeof Home;
  /** Allow-list of roles; omitted = visible to everyone. */
  roles?: Role[];
  /** Surfaced in the mobile bottom tab bar (capped); all items live in More. */
  primary?: boolean;
  /** Only show when the user is a crew member (a player-coach supervisor is one
   *  too) — keeps crew self-views off a desk supervisor's nav. */
  requiresCrewMember?: boolean;
  /** Section this item falls under when the nav renders grouped. Items with no
   *  group (Home) pin to the top, above the first header. */
  group?: NavGroup;
}

// Order here is the canonical source order: it drives the flat list shown to
// short-nav roles AND the within-section order when the nav renders grouped
// (sections themselves order by NAV_GROUPS). It also drives the mobile
// bottom-tab order via the `primary` slice, so keep Home → self-views →
// Projects → Calendar → Dispatch ahead of the rest.
export const NAV: NavItem[] = [
  { to: '/', label: 'Home', icon: Home, roles: ['supervisor', 'pm', 'sales', 'admin'], primary: true },
  { to: '/today', label: 'Today', icon: MapPin, roles: ['crew', 'supervisor', 'admin'], primary: true, requiresCrewMember: true, group: 'my-work' },
  { to: '/my-week', label: 'My week', icon: CalendarDays, roles: ['crew', 'supervisor', 'admin'], primary: true, requiresCrewMember: true, group: 'my-work' },
  { to: '/crew-chat', label: 'Crew chat', icon: MessagesSquare, roles: ['crew', 'supervisor', 'admin'], group: 'my-work' },
  { to: '/alerts', label: 'Alerts', icon: Bell, roles: ['crew'], primary: true, group: 'my-work' },
  { to: '/profile', label: 'Me', icon: User, roles: ['crew'], primary: true, group: 'my-work' },
  { to: '/projects', label: 'Projects', icon: ClipboardList, roles: ['supervisor', 'pm', 'sales', 'admin'], primary: true, group: 'operations' },
  // Schedule stays in the full nav, but no longer rides the mobile bottom bar —
  // Calendar takes its bottom-tab slot (below).
  { to: '/schedule', label: 'Schedule', icon: Calendar, roles: ['supervisor', 'admin'], group: 'operations' },
  // Shared, all-roles project calendar (no `roles` = visible to everyone).
  // Primary so it rides the mobile bottom tab bar in Schedule's place.
  { to: '/calendar', label: 'Calendar', icon: CalendarRange, primary: true, group: 'operations' },
  { to: '/dispatch', label: 'Dispatch', icon: Truck, roles: ['supervisor', 'admin'], primary: true, group: 'operations' },
  { to: '/pickups', label: 'Pickups', icon: PackageCheck, roles: ['supervisor', 'admin'], group: 'operations' },
  { to: '/crew', label: 'Crew', icon: Users, roles: ['supervisor', 'admin'], group: 'resources' },
  { to: '/fleet', label: 'Fleet', icon: Car, roles: ['supervisor', 'admin'], group: 'resources' },
  { to: '/activity', label: 'Activity', icon: Activity, roles: ['supervisor', 'admin'], group: 'reporting' },
  { to: '/insights', label: 'Insights', icon: BarChart3, roles: ['supervisor', 'admin'], group: 'reporting' },
  { to: '/notifications-log', label: 'Notifications', icon: Send, roles: ['supervisor', 'admin'], group: 'admin' },
  { to: '/admin/access-requests', label: 'Users', icon: ShieldCheck, roles: ['admin'], group: 'admin' },
  { to: '/settings', label: 'Settings', icon: Settings, group: 'admin' },
];

export const MAX_PRIMARY = 4;

/** The nav items visible to a role, and the subset shown as mobile bottom tabs. */
export function navForRole(
  role: Role | undefined,
  isCrewMember = false,
): {
  visible: NavItem[];
  primary: NavItem[];
} {
  const visible = NAV.filter(
    (n) =>
      (!n.roles || (!!role && n.roles.includes(role))) &&
      (!n.requiresCrewMember || isCrewMember),
  );
  const primary = visible.filter((n) => n.primary).slice(0, MAX_PRIMARY);
  return { visible, primary };
}

/** At or below this many visible items, section headers add more noise than
 *  structure — render one flat list. Tuned so crew (6) and PM/Sales (4) stay
 *  flat while supervisor (11–13) and admin (12–14) get grouped. */
export const GROUP_THRESHOLD = 7;

export interface NavSection {
  /** null for the pinned, header-less run of items above the first section. */
  key: NavGroup | null;
  label: string | null;
  items: NavItem[];
}

/** Split a role's visible items into render sections. Short lists collapse to a
 *  single header-less section (a flat list); long lists pin the group-less items
 *  (Home) on top, then emit one section per non-empty group in NAV_GROUPS order.
 *  Within a section, items keep their NAV source order. */
export function navSections(visible: NavItem[]): NavSection[] {
  if (visible.length <= GROUP_THRESHOLD) {
    return [{ key: null, label: null, items: visible }];
  }
  const sections: NavSection[] = [];
  const pinned = visible.filter((n) => !n.group);
  if (pinned.length) sections.push({ key: null, label: null, items: pinned });
  for (const { key, label } of NAV_GROUPS) {
    const items = visible.filter((n) => n.group === key);
    if (items.length) sections.push({ key, label, items });
  }
  return sections;
}
